import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ShippingManifestPermissions, VesselNotificationPermissions } from '@common/classes/permissions';
import { AppControlType } from '@common/components/app-control/app-control.component';
import { AppGridComponent } from '@common/components/app-grid/app-grid.component';
import { User } from '@common/models/User';
import { BreezeViewService } from '@common/services/breeze-view.service';
import { DialogService } from '@common/services/dialog.service';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { VesselNotificationDefectsModalComponent } from '../vessel-notification-defects-modal/vessel-notification-defects-modal.component';
import { CargoAgentDataConfirmationModalComponent } from './../../../shipping-manifest/cargo-agent-data-confirmation-modal/cargo-agent-data-confirmation-modal.component';

@Component({
    selector: 'app-vessel-notification-grid',
    templateUrl: './vessel-notification-grid.component.html',
    styleUrls: ['./vessel-notification-grid.component.scss']
})
export class VesselNotificationGridComponent {
    @Input() query;
    @Input() selection = [];
    @Input() user: User;
    @Input() disableGridClickFilter: boolean;
    @Input() isManifest: boolean = false;
    @Input() sort = [{ field: 'id', dir: 'asc' }] as SortDescriptor[];
    @Input() isBusy: boolean = false;

    @Output() dataChange = new EventEmitter();
    @Output() rowParameterClicked = new EventEmitter();

    @ViewChild(AppGridComponent) public appGrid: AppGridComponent;

    constructor(private dialogService: DialogService,
        private breezeViewService: BreezeViewService) { }

    canSeeRemarks(vesselNotification) {
        const hasPermission = this.user?.hasPermission(VesselNotificationPermissions.View.remarks);
        if (this.user?.isAgent()) return hasPermission && !!vesselNotification.portRemarks;
        return hasPermission;
    }

    async openDefectsModal(vnId) {
        this.dialogService.open(VesselNotificationDefectsModalComponent, { size: 'md' },
            dialogRef => dialogRef.componentInstance.vesselNotificationId = vnId);
    }

    canUploadManifest(vesselNotification: any) {
        return (!['COM', 'NOCA'].includes(vesselNotification.manifestStatusId) || this.user.isSystemUser)
            && this.user.hasPermission(ShippingManifestPermissions.Action.upload);
    }

    async uploadManifest(vesselNotificationId: any, agents: any[]) {
        if (!agents.some(x => x.agentRoleId === 'CA' && x.agentId === this.user.organizationId) && !this.user?.isSystemUser) return this.breezeViewService.toastrService.error('Only Cargo Agents can upload manifests.');
        const data = await this.dialogService.form(
            {
                title: 'Upload file',
                properties: [
                    {
                        label: 'File',
                        name: 'file',
                        type: AppControlType.File,
                        allowedExtensions: ['ccs', 'gol', 'mms', 'tcs', 'txt', 'xml', 'rip', 'hms', 'mma', 'sum', 'edi'],
                    }
                ],
                message: 'Accepted file extensions: .ccs, .gol, .mms, .tcs, .txt, .xml, .rip, .hms, .mma, .sum, .edi',
                canConfirm: (model) => model.file
            }, { size: 'md' });
        if (!data) return;

        this.appGrid.isBusy = true;
        try {
            const commandName = data.file.name.endsWith('.edi') ? 'UploadEdifact' : 'UploadManifest';
            const isEurostatUpload = data.file.name.endsWith('.edi');
            const res = await this.breezeViewService.executeCommand(commandName, { ...data.file, vesselNotificationId, isEurostatUpload }, true, true, false);
            if (res.results[0] > 0)
                await this.dialogService.warning('Warning', `${res} records without valid Commodity Type! Classified as other unidentifiable goods.`);
            this.dataChange.emit();
            this.appGrid.isBusy = false;
        } catch (err) {
            this.appGrid.isBusy = false;
        }
    }

    hasCargoConfirmation(confirmations: any[]): boolean {
        return confirmations.some(x => x.confirmedCargo && x.confirmedById === this.user.id);
    }

    async openRemarksModal(vesselNotification) {
        const data = await this.dialogService.form(
            {
                title: 'Vessel Notification Remarks',
                properties: [
                    {
                        label: 'Remarks',
                        name: 'portRemarks',
                        type: AppControlType.TextArea,
                        initialValue: vesselNotification.portRemarks,
                        isDisabled: () => !this.user?.hasPermission(VesselNotificationPermissions.Edit.remarks)
                            || (vesselNotification.portRemarks && vesselNotification.portRemarksViewed && !vesselNotification.portRemarksViewedByTM)
                    }
                ],
                confirmText: 'Save',
            }, { size: 'md', keyboard: false });
        if (!data) {
            return;
        }
        this.breezeViewService.handleCommand('SetVesselNotificationPortRemarks', { id: vesselNotification.id, portRemarks: data.portRemarks })
            .then(() => this.dataChange.emit());
    }

    isRunningLate(row): number {
        return (row.notificationTypeId === 'A' && row.statusId !== 'AR') ||
            (row.notificationTypeId === 'D' && row.statusId !== 'DP') ?
                (Date.parse(row[`portOfCallEt${row.notificationTypeId.toLowerCase()}`]) - Date.now()) / 3_600_000 : 9001;
    }

    emitFilterData(dataItem: any, propertyName: string) {
        if (this.disableGridClickFilter) return;
        this.rowParameterClicked.emit({ dataItem, propertyName });
    }

    isExpired(date, visitStatus) {
        return Date.parse(date) < Date.now() && visitStatus !== 'DE';
    }

    public getRowClass(context: RowClassArgs) {
        const isExpired = Date.parse(context.dataItem.portOfCallEtd) < Date.now() && context.dataItem?.vesselVisit?.statusId !== 'DE';
        return { expiredETD: isExpired };
    }

    isRestricted(statusId: string, showToolTip = false) {
        return showToolTip ? statusId === 'DT' ? 'Detained Vessel' : 'Alert Vessel' : ['DT', 'BN'].includes(statusId);
    }

    async getCargoAgentConfirmedData(id) {
        if (!this.user.hasPermission(ShippingManifestPermissions.View.all)) return;
        const res = await this.dialogService.open(CargoAgentDataConfirmationModalComponent, { size: 'md' }, dialogRef => dialogRef.componentInstance.id = id);
        if (!res) return;
        this.dataChange.emit();
    }

    isOpsConnected(vesselVisit: any) {
        return vesselVisit?.energyConsumptions.some(x => !x.disconnectionTimestamp);
    }
}
